// In-game HUD - designer-team pass (UI_REDESIGN_PLAN U4/U5).
// Routes panels through THEME.panel() and lifts headlines to display font.
import Phaser from "phaser";
import {
  GAME_HEIGHT,
  GAME_WIDTH,
  TOWERS,
  TowerStats,
  getTowerBuildCost,
  getTowerLevelStats
} from "../config";
import { THEME, panel } from "../ui/theme";
import { Economy } from "../systems/Economy";
import { TowerPlacement } from "../systems/TowerPlacement";
import { WaveManager } from "../systems/WaveManager";
import { Tower } from "../entities/Tower";
import { GameScene } from "./GameScene";
import { save } from "../systems/Save";
import { sound } from "../systems/Sound";
import { AbilityController, AbilityState } from "../systems/AbilityController";
import { achievementToast } from "../ui/achievementToast";

/** Shrink a Phaser text object uniformly so it fits within `maxW` pixels.
 *  No-op when it already fits. Keeps origin / position intact. */
function fitTextWidth(text: Phaser.GameObjects.Text, maxW: number): void {
  text.setScale(1);
  if (text.width <= maxW) return;
  const s = Math.max(0.5, maxW / text.width);
  text.setScale(s);
}

export class UIScene extends Phaser.Scene {
  private goldText!: Phaser.GameObjects.Text;
  private livesText!: Phaser.GameObjects.Text;
  private waveText!: Phaser.GameObjects.Text;
  private startWaveBtnLabel!: Phaser.GameObjects.Text;
  private startWaveBtnPanel!: Phaser.GameObjects.NineSlice;
  private speedBtnLabel!: Phaser.GameObjects.Text;
  private autoBtnLabel!: Phaser.GameObjects.Text;
  private autoBtnPanel!: Phaser.GameObjects.NineSlice;
  private overlayGroup?: Phaser.GameObjects.Container;
  /** Set while an overlay can be dismissed with ESC instead of only by click. */
  private overlayDismiss?: () => void;
  private selectedKey: string | null = null;
  private towerButtons: Phaser.GameObjects.Container[] = [];
  private upgradePanel?: Phaser.GameObjects.Container;
  private selectedTower: Tower | null = null;
  private upgradeAffordable: boolean | null = null;
  private placement!: TowerPlacement;
  private displayedGold = 0;
  private displayedLives = 0;
  private cleanupFns: Array<() => void> = [];

  constructor() { super({ key: "UIScene", active: false }); }

  create(): void {
    this.runCleanup();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.runCleanup());
    achievementToast.attach(this);

    // Phaser reuses the same Scene class instance across shutdown/start, so
    // any field that holds references to GameObjects from a previous run
    // must be reset here — otherwise callbacks (e.g. economy.on, which fires
    // immediately) iterate destroyed objects and crash with `setVisible` of
    // undefined.
    this.overlayGroup = undefined;
    this.overlayDismiss = undefined;
    this.selectedKey = null;
    this.towerButtons = [];
    this.upgradePanel = undefined;
    this.selectedTower = null;
    this.upgradeAffordable = null;
    this.displayedGold = 0;
    this.displayedLives = 0;
    this.cleanupFns = [];
    this.trackedBoss = null;
    this.bossBarBg = undefined;
    this.bossBarFill = undefined;
    this.bossBarLabel = undefined;
    this.bossBarFullW = 0;
    this.bossBarShownHp = -1;
    this.abilityCells = [];
    this.abilityCtlRef = null;
    this.tutorialContainer = undefined;
    this.tutorialPortrait = undefined;
    this.tutorialText = undefined;
    this.tutorialNextLabel = undefined;
    this.tutorialStep = 0;

    const game = this.scene.get("GameScene") as GameScene;
    const economy = game.registry.get("economy") as Economy;
    const waveManager = game.registry.get("waveManager") as WaveManager;
    const placement = game.registry.get("placement") as TowerPlacement;
    this.placement = placement;

    const onKey = (event: string, fn: () => void) => {
      this.input.keyboard?.on(event, fn);
      this.cleanupFns.push(() => this.input.keyboard?.off(event, fn));
    };

    // ---- Top status bar ----
    const topBar = panel(this, 0, 0, GAME_WIDTH, 40, "iron-bar").setDepth(THEME.depths.hud);
    topBar.setInteractive(); // block clicks from reaching game

    this.goldText  = this.add.text(20, 20, "Gold: 0",
      { fontFamily: THEME.fonts.display, fontSize: "20px", color: THEME.colors.gold }).setOrigin(0, 0.5).setDepth(THEME.depths.hud);
    this.livesText = this.add.text(190, 20, "Lives: 0",
      { fontFamily: THEME.fonts.display, fontSize: "20px", color: THEME.colors.parchment }).setOrigin(0, 0.5).setDepth(THEME.depths.hud);
    this.waveText  = this.add.text(360, 20, "Wave: 0/0",
      { fontFamily: THEME.fonts.display, fontSize: "20px", color: THEME.colors.parchment }).setOrigin(0, 0.5).setDepth(THEME.depths.hud);

    // Right-side controls.
    const startW = 240, startH = 30;
    const startX = GAME_WIDTH - startW - 8;
    const startY = 5;
    this.startWaveBtnPanel = panel(this, startX, startY, startW, startH, "iron").setDepth(THEME.depths.hud);
    this.startWaveBtnPanel.setInteractive({ useHandCursor: true });
    this.startWaveBtnLabel = this.add.text(startX + startW / 2, startY + startH / 2, "START WAVE", {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.gold
    }).setOrigin(0.5).setDepth(THEME.depths.hud);
    this.startWaveBtnPanel.on("pointerover", () => { this.startWaveBtnPanel.setTint(0xffeec0); this.startWaveBtnLabel.setColor(THEME.colors.parchment); });
    this.startWaveBtnPanel.on("pointerout",  () => { this.startWaveBtnPanel.clearTint(); this.startWaveBtnLabel.setColor(THEME.colors.gold); });
    this.startWaveBtnPanel.on("pointerdown", () => waveManager.startNextWave());

    const autoW = 110, autoH = 30;
    const autoX = startX - autoW - 8;
    this.autoBtnPanel = panel(this, autoX, startY, autoW, autoH, "iron").setDepth(THEME.depths.hud);
    this.autoBtnPanel.setInteractive({ useHandCursor: true });
    this.autoBtnLabel = this.add.text(autoX + autoW / 2, startY + autoH / 2, "AUTO: OFF", {
      fontFamily: THEME.fonts.display, fontSize: "15px", color: THEME.colors.parchmentDim
    }).setOrigin(0.5).setDepth(THEME.depths.hud);
    const refreshAutoBtn = () => {
      const on = waveManager.isAutoContinue;
      this.autoBtnLabel.setText(on ? "AUTO: ON" : "AUTO: OFF");
      this.autoBtnLabel.setColor(on ? THEME.colors.gold : THEME.colors.parchmentDim);
    };
    this.autoBtnPanel.on("pointerover", () => this.autoBtnPanel.setTint(0xffeec0));
    this.autoBtnPanel.on("pointerout",  () => this.autoBtnPanel.clearTint());
    this.autoBtnPanel.on("pointerdown", () => { waveManager.setAutoContinue(!waveManager.isAutoContinue); refreshAutoBtn(); });
    refreshAutoBtn();
    // Gated on overlayGroup: a modal owns the screen, and toggling auto-continue
    // behind the campaign overlay would roll the run into Endless unasked.
    onKey("keydown-A", () => {
      if (this.overlayGroup) return;
      waveManager.setAutoContinue(!waveManager.isAutoContinue);
      refreshAutoBtn();
    });

    const speedW = 60, speedH = 30;
    const speedX = autoX - speedW - 8;
    const speedPanel = panel(this, speedX, startY, speedW, speedH, "iron").setDepth(THEME.depths.hud);
    speedPanel.setInteractive({ useHandCursor: true });
    this.speedBtnLabel = this.add.text(speedX + speedW / 2, startY + speedH / 2, "1x", {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.frost
    }).setOrigin(0.5).setDepth(THEME.depths.hud);
    speedPanel.on("pointerover", () => speedPanel.setTint(0xffeec0));
    speedPanel.on("pointerout",  () => speedPanel.clearTint());
    speedPanel.on("pointerdown", () => game.cycleSpeed());
    const updateSpeedLabel = (s: number) => this.speedBtnLabel.setText(`${s}x`);
    updateSpeedLabel(game.getSpeed());
    game.events.on("speed-changed", updateSpeedLabel);
    this.cleanupFns.push(() => game.events.off("speed-changed", updateSpeedLabel));
    onKey("keydown-SPACE", () => { if (!this.overlayGroup) game.cycleSpeed(); });

    const escW = 60, escH = 30;
    const escX = speedX - escW - 8;
    const escPanel = panel(this, escX, startY, escW, escH, "iron").setDepth(THEME.depths.hud);
    escPanel.setInteractive({ useHandCursor: true });
    const escLabel = this.add.text(escX + escW / 2, startY + escH / 2, "ESC", {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.blood
    }).setOrigin(0.5).setDepth(THEME.depths.hud);
    escPanel.on("pointerover", () => escPanel.setTint(0xffeec0));
    escPanel.on("pointerout",  () => escPanel.clearTint());
    escPanel.on("pointerdown", () => this.handleEscape());
    void escLabel;

    const menuW = 90, menuH = 30;
    const menuX = escX - menuW - 8;
    const menuPanel = panel(this, menuX, startY, menuW, menuH, "iron").setDepth(THEME.depths.hud);
    menuPanel.setInteractive({ useHandCursor: true });
    const menuLabel = this.add.text(menuX + menuW / 2, startY + menuH / 2, "MENU", {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.gold
    }).setOrigin(0.5).setDepth(THEME.depths.hud);
    menuPanel.on("pointerover", () => { menuPanel.setTint(0xffeec0); menuLabel.setColor(THEME.colors.parchment); });
    menuPanel.on("pointerout",  () => { menuPanel.clearTint(); menuLabel.setColor(THEME.colors.gold); });
    menuPanel.on("pointerdown", () => this.confirmQuitToMenu(game));

    // Music / mute toggle, sits left of MENU. Mirrors the menu's mute pill.
    const muteW = 40, muteH = 30;
    const muteX = menuX - muteW - 8;
    const mutePanel = panel(this, muteX, startY, muteW, muteH, "iron").setDepth(THEME.depths.hud);
    mutePanel.setInteractive({ useHandCursor: true });
    const muteLabel = this.add.text(muteX + muteW / 2, startY + muteH / 2, sound.isMuted() ? "\uD83D\uDD07" : "\uD83D\uDD0A", {
      fontFamily: THEME.fonts.display, fontSize: "18px",
      color: sound.isMuted() ? THEME.colors.blood : THEME.colors.gold
    }).setOrigin(0.5).setDepth(THEME.depths.hud);
    const refreshMute = () => {
      const m = sound.isMuted();
      muteLabel.setText(m ? "\uD83D\uDD07" : "\uD83D\uDD0A");
      muteLabel.setColor(m ? THEME.colors.blood : THEME.colors.gold);
    };
    mutePanel.on("pointerover", () => mutePanel.setTint(0xffeec0));
    mutePanel.on("pointerout",  () => mutePanel.clearTint());
    mutePanel.on("pointerdown", () => { sound.toggleMuted(); refreshMute(); });
    const offMute = sound.onChange(refreshMute);
    this.cleanupFns.push(offMute);
    onKey("keydown-M", () => { sound.toggleMuted(); refreshMute(); });

    this.cleanupFns.push(economy.on(s => {
      this.tweenNumber(this.displayedGold, s.gold, 200, v => { this.displayedGold = v; this.goldText.setText(`Gold: ${v}`); });
      this.tweenNumber(this.displayedLives, s.lives, 200, v => { this.displayedLives = v; this.livesText.setText(`Lives: ${v}`); });
      this.refreshTowerButtons(economy);
    }));
    this.displayedGold = economy.gold;
    this.displayedLives = economy.lives;
    this.goldText.setText(`Gold: ${this.displayedGold}`);
    this.livesText.setText(`Lives: ${this.displayedLives}`);

    const waveLabel = () => {
      const total = waveManager.isEndlessMode && waveManager.currentWaveNumber > waveManager.totalWaves
        ? "Endless"
        : String(waveManager.totalWaves);
      return `Wave: ${waveManager.currentWaveNumber}/${total}`;
    };

    this.cleanupFns.push(waveManager.on(e => {
      this.waveText.setText(waveLabel());
      const visible = !waveManager.isInWave && waveManager.hasMoreWaves;
      this.startWaveBtnPanel.setVisible(visible);
      this.startWaveBtnLabel.setVisible(visible);
      if (e.kind === "wave-cleared") {
        economy.reward(e.reward);
        this.flashMessage(`Wave ${e.index + 1} cleared! +${e.reward} gold`, THEME.colors.gold);
      }
      if (e.kind === "auto-countdown") {
        const secs = Math.ceil(e.remainingMs / 1000);
        this.startWaveBtnLabel.setText(`AUTO START in ${secs}s`);
      } else if (e.kind === "wave-start") {
        this.startWaveBtnLabel.setText("START WAVE");
      } else if (e.kind === "wave-cleared") {
        this.startWaveBtnLabel.setText("START WAVE");
      }
      // GameScene owns terminal run state. The HUD only reflects it.
    }));
    this.waveText.setText(`Wave: 0/${waveManager.totalWaves}`);

    // ---- Bottom tower hotbar ----
    const hotbar = panel(this, 0, GAME_HEIGHT - 80, GAME_WIDTH, 80, "iron-bar").setDepth(THEME.depths.hud);
    hotbar.setInteractive();

    const towerList = Object.values(TOWERS);
    const slotW = 160;
    const totalW = slotW * towerList.length;
    const startSx = (GAME_WIDTH - totalW) / 2;

    towerList.forEach((t, i) => {
      const btn = this.makeTowerButton(t, i + 1, startSx + i * slotW + slotW / 2, GAME_HEIGHT - 40, () => {
        const required = t.unlockLevel ?? 1;
        if (save.get().playerLevel < required) {
          this.flashMessage(`${t.name} unlocks at Commander Lvl ${required}`, THEME.colors.forge);
          return;
        }
        if (this.selectedKey === t.key) {
          placement.select(null);
          this.selectedKey = null;
        } else {
          placement.select(t);
          this.selectedKey = t.key;
        }
        sound.playUi("click");
        this.refreshTowerButtons(economy);
      });
      this.towerButtons.push(btn);
    });
    this.refreshTowerButtons(economy);

    const onTowerSelected = (tower: Tower) => this.showUpgradePanel(tower, economy);
    const onTowerUpgraded = (tower: Tower) => {
      if (this.selectedTower === tower) this.showUpgradePanel(tower, economy);
    };
    const onPlaceFailed = (p: { reason: "blocked" | "gold" }) => {
      this.flashMessage(p.reason === "gold" ? "Not enough gold" : "Can't build there", THEME.colors.blood);
    };
    game.events.on("tower:selected", onTowerSelected);
    game.events.on("tower:upgraded", onTowerUpgraded);
    game.events.on("tower:place-failed", onPlaceFailed);
    this.cleanupFns.push(
      () => game.events.off("tower:selected", onTowerSelected),
      () => game.events.off("tower:upgraded", onTowerUpgraded),
      () => game.events.off("tower:place-failed", onPlaceFailed)
    );

    this.cleanupFns.push(economy.on(() => {
      // Only refresh the upgrade panel when affordability actually flips,
      // otherwise it would rebuild (and re-tween) on every gold change.
      if (!this.selectedTower) return;
      const cost = this.selectedTower.nextUpgradeCost;
      const aff = cost == null ? null : economy.canAfford(cost);
      if (aff !== this.upgradeAffordable) this.showUpgradePanel(this.selectedTower, economy);
    }));
    onKey("keydown-ESC", () => this.handleEscape());

    const onGameOver = (data: { victory: boolean }) => this.showGameOver(data.victory, game);
    game.events.on("game-over", onGameOver);
    this.cleanupFns.push(() => game.events.off("game-over", onGameOver));

    const onPlayerLevelUp = (p: { levels: number; save: { playerLevel: number } }) => {
      this.showLevelUpToast(p.save.playerLevel, p.levels);
      this.refreshTowerButtons(economy);
    };
    const onCampaignSecured = (p: { wave: number; mapName: string; nextMapName: string | null }) => {
      this.showCampaignSecured(p, game);
    };
    const onEndlessMilestone = (p: { wave: number; gold: number; xp: number }) => {
      this.flashMessage(`Endless Wave ${p.wave}: +${p.gold}g  +${p.xp} XP`, THEME.colors.frost);
    };
    const onHotkeyLocked = (p: { tower: TowerStats; required: number }) => {
      this.flashMessage(`${p.tower.name} unlocks at Commander Lvl ${p.required}`, THEME.colors.forge);
    };
    game.events.on("player-level-up", onPlayerLevelUp);
    game.events.on("campaign-secured", onCampaignSecured);
    game.events.on("endless-milestone", onEndlessMilestone);
    game.events.on("hotkey-locked", onHotkeyLocked);
    this.cleanupFns.push(
      () => game.events.off("player-level-up", onPlayerLevelUp),
      () => game.events.off("campaign-secured", onCampaignSecured),
      () => game.events.off("endless-milestone", onEndlessMilestone),
      () => game.events.off("hotkey-locked", onHotkeyLocked)
    );

    this.makeBossBar();
    const onBossTracked = (boss: import("../entities/Enemy").Enemy | null) => {
      this.trackedBoss = boss;
      this.refreshBossBar();
    };
    game.events.on("boss-tracked", onBossTracked);
    this.cleanupFns.push(() => game.events.off("boss-tracked", onBossTracked));

    const abilityCtl = game.registry.get("abilityController") as AbilityController | undefined;
    if (abilityCtl) this.makeAbilityBar(abilityCtl);

    // Tutorial overlay (first run, or forced via Menu "TUTORIAL" test button).
    if (this.registry.get("runTutorial") === true) {
      this.startTutorial();
    }
  }

  private runCleanup(): void {
    const fns = this.cleanupFns.splice(0);
    for (const fn of fns) {
      try { fn(); } catch { /* ignore stale cleanup */ }
    }
    this.overlayGroup?.destroy();
    this.overlayGroup = undefined;
    this.upgradePanel?.destroy();
    this.upgradePanel = undefined;
    this.tutorialContainer?.destroy();
    this.tutorialContainer = undefined;
  }

  private trackedBoss: import("../entities/Enemy").Enemy | null = null;
  private bossBarBg?: Phaser.GameObjects.NineSlice;
  private bossBarFill?: Phaser.GameObjects.Rectangle;
  private bossBarLabel?: Phaser.GameObjects.Text;
  private bossBarFullW = 0;
  private bossBarShownHp = -1;

  private makeBossBar(): void {
    const w = 720, h = 24;
    const x = (GAME_WIDTH - w) / 2;
    const y = 46;
    this.bossBarBg = panel(this, x, y, w, h, "iron-thin").setDepth(THEME.depths.boss).setVisible(false);
    this.bossBarFullW = w - 12;
    this.bossBarFill = this.add.rectangle(x + 6, y + 6, this.bossBarFullW, h - 12, THEME.hex.blood)
      .setOrigin(0).setVisible(false).setDepth(THEME.depths.boss + 1);
    this.bossBarLabel = this.add.text(GAME_WIDTH / 2, y + h / 2, "", {
      fontFamily: THEME.fonts.display, fontSize: "15px", color: THEME.colors.parchment,
      stroke: THEME.colors.ironDeep, strokeThickness: 4
    }).setOrigin(0.5).setVisible(false).setDepth(THEME.depths.boss + 2);
  }

  private refreshBossBar(): void {
    const boss = this.trackedBoss;
    const visible = !!(boss && boss.alive);
    this.bossBarBg?.setVisible(visible);
    this.bossBarFill?.setVisible(visible);
    this.bossBarLabel?.setVisible(visible);
    if (visible && boss && this.bossBarFill && this.bossBarLabel) {
      const pct = Math.max(0, boss.hp / boss.maxHp);
      this.bossBarFill.width = this.bossBarFullW * pct;
      // setText re-renders the text canvas — only pay for it when the shown HP changes.
      const shownHp = Math.ceil(boss.hp);
      if (shownHp !== this.bossBarShownHp) {
        this.bossBarShownHp = shownHp;
        this.bossBarLabel.setText(`${boss.stats.key.replace("boss_", "").toUpperCase().replace("_", " ")}    ${shownHp} / ${boss.maxHp}`);
      }
    }
  }

  update(): void {
    if (this.trackedBoss) this.refreshBossBar();
    if (this.abilityCells.length > 0) this.refreshAbilityBar();
  }

  // --- Ability hotbar ---
  private abilityCells: { state: AbilityState; bg: Phaser.GameObjects.NineSlice; icon: Phaser.GameObjects.Image; cd: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text; key: Phaser.GameObjects.Text; tooltip: Phaser.GameObjects.Text; shownReady?: boolean }[] = [];
  private abilityCtlRef: AbilityController | null = null;

  private makeAbilityBar(ctl: AbilityController): void {
    this.abilityCtlRef = ctl;
    const cellW = 56, cellH = 56, gap = 6;
    const states = ctl.snapshot();
    const visible = states.filter(s => s.unlocked);
    if (visible.length === 0) {
      this.cleanupFns.push(ctl.on(() => { /* no-op */ }));
      return;
    }
    // Dock commander abilities directly below the Start Wave controls so the
    // tower picker and the lower build area remain completely unobstructed.
    const barW = visible.length * cellW + Math.max(0, visible.length - 1) * gap;
    const startX = GAME_WIDTH - 16 - barW;
    const y = 48;

    visible.forEach((state, i) => {
      const x = startX + i * (cellW + gap);
      const bg = panel(this, x, y, cellW, cellH, "iron").setDepth(THEME.depths.hud);
      const icon = this.add.image(x + cellW / 2, y + cellH / 2 - 3, `ability-${state.key}`)
        .setDisplaySize(40, 40).setDepth(THEME.depths.hud + 1);
      const cd = this.add.rectangle(x + 3, y + cellH - 3, cellW - 6, 0, 0x000000, 0.7)
        .setOrigin(0, 1).setDepth(THEME.depths.hud + 2);
      const label = this.add.text(x + cellW / 2, y + cellH - 5, state.label, {
        fontFamily: THEME.fonts.display, fontSize: "10px", color: THEME.colors.gold,
        stroke: THEME.colors.ironDeep, strokeThickness: 2
      }).setOrigin(0.5, 1).setDepth(THEME.depths.hud + 3);
      const key = this.add.text(x + 6, y + 4, state.hotkey, {
        fontFamily: THEME.fonts.display, fontSize: "15px", color: THEME.colors.parchment,
        stroke: THEME.colors.ironDeep, strokeThickness: 3
      }).setOrigin(0).setDepth(THEME.depths.hud + 3);
      const tooltip = this.add.text(x + cellW, y + cellH + 8, state.description, {
        fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.parchment,
        backgroundColor: "#000000cc", padding: { x: 8, y: 6 }, wordWrap: { width: 240 }
      }).setOrigin(1, 0).setDepth(THEME.depths.tooltip).setVisible(false);
      bg.setInteractive({ useHandCursor: true });
      bg.on("pointerover", () => { tooltip.setVisible(true); bg.setTint(0xffeec0); });
      bg.on("pointerout",  () => { tooltip.setVisible(false); bg.clearTint(); });
      bg.on("pointerdown", () => ctl.cast(state.key, this.input.activePointer));
      this.abilityCells.push({ state, bg, icon, cd, label, key, tooltip });
    });
    this.refreshAbilityBar();
  }

  private refreshAbilityBar(): void {
    if (!this.abilityCtlRef) return;
    const states = this.abilityCtlRef.snapshot();
    for (const cell of this.abilityCells) {
      const cur = states.find(s => s.key === cell.state.key);
      if (!cur) continue;
      const pct = cur.cooldownMs > 0 ? cur.remainingMs / cur.cooldownMs : 0;
      cell.cd.height = (cell.bg.height - 6) * pct;
      // setColor re-renders the label canvas — only pay for it on ready-state flips.
      if (cur.ready !== cell.shownReady) {
        cell.shownReady = cur.ready;
        cell.label.setColor(cur.ready ? THEME.colors.gold : THEME.colors.lock);
        cell.icon.setAlpha(cur.ready ? 1 : 0.48);
      }
    }
  }

  private showLevelUpToast(newLevel: number, gainedLevels: number): void {
    const cx = GAME_WIDTH / 2, y0 = 90;
    const text = this.add.text(cx, y0,
      `LEVEL UP! Commander Lvl ${newLevel} (+${gainedLevels} skill point${gainedLevels === 1 ? "" : "s"})`, {
      fontFamily: THEME.fonts.display, fontSize: "22px", color: THEME.colors.gold,
      stroke: THEME.colors.ironDeep, strokeThickness: 4
    }).setOrigin(0.5).setAlpha(0).setDepth(THEME.depths.toast);
    this.tweens.add({
      targets: text, alpha: 1, y: y0 + 8, duration: 220,
      onComplete: () => {
        this.tweens.add({
          targets: text, alpha: 0, y: y0 - 16, delay: 1600, duration: 600,
          onComplete: () => text.destroy()
        });
      }
    });
  }

  private makeTowerButton(t: TowerStats, hotkey: number, x: number, y: number, onClick: () => void): Phaser.GameObjects.Container {
    const c = this.add.container(x, y).setDepth(THEME.depths.hud + 1);
    const w = 154, h = 74;
    const bg = panel(this, -w / 2, -h / 2, w, h, "iron");
    bg.setInteractive({ useHandCursor: true });

    const buildCost = getTowerBuildCost(t);
    const lvl1 = getTowerLevelStats(t, 1);

    const keyText = this.add.text(-w / 2 + 8, -h / 2 + 6, String(hotkey), {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.gold,
      stroke: THEME.colors.ironDeep, strokeThickness: 3
    }).setOrigin(0, 0);

    const icon = this.add.image(-w / 2 + 40, 4, `tower-${t.key}-1`).setDisplaySize(50, 50);

    // Text area starts to the right of the icon. Reserve room on the right
    // for the cost label so nothing overlaps.
    const textX  = -w / 2 + 70;
    const textRight = w / 2 - 8;
    const nameW  = textRight - textX;          // full remaining width for name
    const statsW = nameW - 32;                  // leave room for cost on bottom-right

    // Name is centered inside the text column so every tower title sits the
    // same way regardless of length.
    const name = this.add.text((textX + textRight) / 2, -h / 2 + 8, t.name, {
      fontFamily: THEME.fonts.display, fontSize: "15px", color: THEME.colors.parchment,
      stroke: THEME.colors.ironDeep, strokeThickness: 2,
      align: "center"
    }).setOrigin(0.5, 0);
    fitTextWidth(name, nameW);

    const stats = this.add.text(textX, -h / 2 + 30,
      `D${lvl1.damage} R${lvl1.range}\n${lvl1.fireRate.toFixed(1)}/s`,
      { fontFamily: THEME.fonts.body, fontSize: "13px", color: THEME.colors.parchmentDim, lineSpacing: 1 }
    ).setOrigin(0, 0);
    fitTextWidth(stats, statsW);

    const costText = this.add.text(w / 2 - 8, h / 2 - 12, `${buildCost}g`, {
      fontFamily: THEME.fonts.display, fontSize: "15px", color: THEME.colors.gold,
      stroke: THEME.colors.ironDeep, strokeThickness: 2
    }).setOrigin(1, 0.5);

    c.add([bg, icon, keyText, name, stats, costText]);

    const lockText = this.add.text(0, 0, `LVL ${t.unlockLevel ?? 1}`, {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.forge
    }).setOrigin(0.5).setVisible(false);
    c.add([lockText]);

    c.setData("bg", bg);
    c.setData("costText", costText);
    c.setData("cost", buildCost);
    c.setData("key", t.key);
    c.setData("unlockLevel", t.unlockLevel ?? 1);
    c.setData("lockText", lockText);
    c.setData("name", name);
    c.setData("stats", stats);

    // Hover tooltip — full stats + description + targeting profile.
    const tooltip = this.makeTowerTooltip(t, x);
    bg.on("pointerover", () => {
      bg.setTint(0xffeec0);
      tooltip.setVisible(true);
    });
    bg.on("pointerout",  () => {
      this.refreshSingleTowerButton(c);
      tooltip.setVisible(false);
    });
    bg.on("pointerdown", onClick);
    return c;
  }

  /** Builds a hover tooltip describing the tower (stats, description, targets).
   *  Anchored above the hotbar, horizontally centered on the button (`btnCenterX`),
   *  clamped inside the screen so it never gets cut off. */
  private makeTowerTooltip(t: TowerStats, btnCenterX: number): Phaser.GameObjects.Container {
    const lvl1 = getTowerLevelStats(t, 1);

    // Targeting / effect profile derived from level-1 stats.
    const targets: string[] = [];
    if (lvl1.splashRadius)      targets.push("• Splash AoE — hits clustered groups");
    if (lvl1.slowFactor)        targets.push("• Slows enemies on hit");
    if (lvl1.dotDps)            targets.push("• Poison DoT — strong vs swarms");
    if (lvl1.pierceCount)       targets.push(`• Pierces up to ${lvl1.pierceCount + 1} enemies in a line`);
    if (lvl1.chainCount)        targets.push(`• Chains to ${lvl1.chainCount} extra targets`);
    if (lvl1.auraFireRateBonus) targets.push(`• Aura: +${Math.round(lvl1.auraFireRateBonus * 100)}% fire rate to nearby towers`);
    if (targets.length === 0)   targets.push("• Single-target damage — works on every ground enemy");

    const lines = [
      `${t.name.toUpperCase()}`,
      `DMG ${lvl1.damage}    RNG ${lvl1.range}    ${lvl1.fireRate.toFixed(1)}/s`,
      ``,
      t.description,
      ``,
      ...targets
    ];

    const w = 240;
    const text = this.add.text(0, 0, lines.join("\n"), {
      fontFamily: THEME.fonts.body,
      fontSize: "13px",
      color: THEME.colors.parchment,
      backgroundColor: "#1a120acc",
      padding: { x: 10, y: 8 },
      wordWrap: { width: w - 20 },
      lineSpacing: 2
    }).setOrigin(0.5, 1).setDepth(THEME.depths.tooltip).setVisible(false);

    // Position above the bottom hotbar; clamp inside the screen.
    const half = text.width / 2;
    const cx = Phaser.Math.Clamp(btnCenterX, half + 6, GAME_WIDTH - half - 6);
    text.setPosition(cx, GAME_HEIGHT - 84);
    return text as unknown as Phaser.GameObjects.Container;
  }

  private refreshSingleTowerButton(c: Phaser.GameObjects.Container): void {
    const bg = c.getData("bg") as Phaser.GameObjects.NineSlice;
    const key = c.getData("key") as string;
    const isSelected = key === this.selectedKey;
    if (isSelected) bg.setTint(0xffe070);
    else bg.clearTint();
  }

  private refreshTowerButtons(economy: Economy): void {
    const playerLevel = save.get().playerLevel;
    for (const btn of this.towerButtons) {
      const bg = btn.getData("bg") as Phaser.GameObjects.NineSlice;
      const costText = btn.getData("costText") as Phaser.GameObjects.Text;
      const baseCost = btn.getData("cost") as number;
      const cost = Math.round(baseCost * economy.modifiers.buildCostMult);
      const key = btn.getData("key") as string;
      const unlockLevel = btn.getData("unlockLevel") as number;
      const lockText = btn.getData("lockText") as Phaser.GameObjects.Text;
      const name = btn.getData("name") as Phaser.GameObjects.Text;
      const stats = btn.getData("stats") as Phaser.GameObjects.Text;

      const locked = playerLevel < unlockLevel;
      const affordable = economy.canAfford(cost);
      const isSelected = key === this.selectedKey;

      lockText.setVisible(locked);
      name.setVisible(!locked);
      stats.setVisible(!locked);
      btn.setAlpha(locked ? 0.55 : 1);
      costText.setText(`${cost}g`);

      if (isSelected) bg.setTint(0xffe070);
      else if (!affordable && !locked) bg.setTint(0x806050);
      else bg.clearTint();

      costText.setColor(locked ? THEME.colors.lock : (affordable ? THEME.colors.gold : THEME.colors.forge));
    }
  }

  private handleEscape(): void {
    // A dismissable overlay owns ESC first — on the campaign screen that means
    // "carry on", which is the same thing the ENTER ENDLESS button does.
    if (this.overlayDismiss) {
      this.overlayDismiss();
      return;
    }
    this.closeUpgradePanel();
    if (this.selectedKey !== null) {
      this.placement.select(null);
      this.selectedKey = null;
      const economy = (this.scene.get("GameScene") as GameScene).registry.get("economy") as Economy;
      this.refreshTowerButtons(economy);
    }
  }

  private flashMessage(msg: string, color: string): void {
    const t = this.add.text(GAME_WIDTH / 2, 80, msg, {
      fontFamily: THEME.fonts.display, fontSize: "26px", color,
      stroke: THEME.colors.ironDeep, strokeThickness: 4,
      backgroundColor: "#000000aa", padding: { x: 16, y: 8 }
    }).setOrigin(0.5).setDepth(THEME.depths.toast);
    this.tweens.add({ targets: t, alpha: 0, y: 60, duration: 1800, onComplete: () => t.destroy() });
  }

  private showUpgradePanel(tower: Tower, economy: Economy): void {
    const wasSameTower = this.selectedTower === tower && this.upgradePanel != null;
    this.closeUpgradePanel();
    this.selectedTower = tower;

    const panelW = 260, panelH = 210;
    const belowY = tower.y + 48;
    const fitsBelow = belowY + panelH < GAME_HEIGHT - 80;
    const py = fitsBelow ? belowY : tower.y - 48 - panelH;
    const px = Phaser.Math.Clamp(tower.x - panelW / 2, 8, GAME_WIDTH - panelW - 8);

    const c = this.add.container(0, 0).setDepth(THEME.depths.overlay);
    const bg = panel(this, px, py, panelW, panelH, "parchment");
    bg.setInteractive(); // swallow clicks

    const cur = tower.current;
    const title = this.add.text(px + 14, py + 10,
      `${tower.stats.name}  Lv ${tower.level}/${tower.maxLevel}`,
      { fontFamily: THEME.fonts.display, fontSize: "18px", color: THEME.colors.iron }).setOrigin(0, 0);

    const stats = [
      `DMG  ${cur.damage}`,
      `RNG  ${cur.range}`,
      `RoF  ${cur.fireRate.toFixed(1)}/s`
    ].join("    ");
    const statsText = this.add.text(px + 14, py + 38, stats,
      { fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.iron }).setOrigin(0, 0);

    const closeBtn = this.add.text(px + panelW - 12, py + 8, "X",
      { fontFamily: THEME.fonts.display, fontSize: "18px", color: THEME.colors.blood })
      .setOrigin(1, 0).setInteractive({ useHandCursor: true });
    closeBtn.on("pointerdown", () => this.closeUpgradePanel());

    c.add([bg, title, statsText, closeBtn]);

    const upCost = tower.nextUpgradeCost;
    if (upCost == null) {
      const maxText = this.add.text(px + panelW / 2, py + 104, "MAX LEVEL",
        { fontFamily: THEME.fonts.display, fontSize: "20px", color: THEME.colors.gold,
          stroke: THEME.colors.ironDeep, strokeThickness: 3 }).setOrigin(0.5);
      c.add(maxText);
    } else {
      const next = getTowerLevelStats(tower.stats, tower.level + 1);
      const preview = this.add.text(px + 14, py + 78,
        `-> DMG ${next.damage}   RNG ${next.range}   RoF ${next.fireRate.toFixed(1)}/s`,
        { fontFamily: THEME.fonts.body, fontSize: "14px", color: "#1a4f1a" }).setOrigin(0, 0);

      const affordable = economy.canAfford(upCost);
      const upW = 200, upH = 32;
      const upX = px + panelW / 2 - upW / 2;
      const upY = py + 102;
      const upPanel = panel(this, upX, upY, upW, upH, "iron");
      upPanel.setInteractive({ useHandCursor: affordable });
      if (!affordable) upPanel.setTint(0x9b5c4b);
      const upText = this.add.text(upX + upW / 2, upY + upH / 2,
        `UPGRADE  ${upCost}g`, {
          fontFamily: THEME.fonts.display, fontSize: "16px",
          color: affordable ? THEME.colors.gold : THEME.colors.forge,
          stroke: THEME.colors.ironDeep,
          strokeThickness: affordable ? 1 : 2
        }).setOrigin(0.5);
      upPanel.on("pointerover", () => { if (affordable) upPanel.setTint(0xffeec0); });
      upPanel.on("pointerout",  () => {
        if (affordable) upPanel.clearTint();
        else upPanel.setTint(0x9b5c4b);
      });
      upPanel.on("pointerdown", () => {
        const cost = tower.nextUpgradeCost;
        if (cost == null) return;
        if (!economy.spend(cost)) {
          this.flashMessage("Not enough gold", THEME.colors.blood);
          return;
        }
        tower.upgrade();
      });

      c.add([preview, upPanel, upText]);
    }

    const refund = economy.quoteSellRefund(tower.totalInvested);
    const refundPct = Math.round(economy.sellRefundPct * 100);
    const sellW = 104, sellH = 26;
    const sellX = px + panelW - sellW - 16;
    const sellY = py + panelH - 42;
    const sellPanel = panel(this, sellX, sellY, sellW, sellH, "iron");
    sellPanel.setTint(0x87332d).setInteractive({ useHandCursor: true });
    const refundText = this.add.text(px + 16, sellY + sellH / 2,
      `Refund ${refundPct}%`, {
        fontFamily: THEME.fonts.body, fontSize: "12px", color: THEME.colors.iron
      }).setOrigin(0, 0.5);
    const sellText = this.add.text(sellX + sellW / 2, sellY + sellH / 2,
      `SELL +${refund}g`, {
        fontFamily: THEME.fonts.display, fontSize: "13px", color: THEME.colors.parchment
      }).setOrigin(0.5);
    sellPanel.on("pointerover", () => sellPanel.setTint(0xa9443b));
    sellPanel.on("pointerout",  () => sellPanel.setTint(0x87332d));
    sellPanel.on("pointerdown", () => {
      sound.playUi("click");
      if (save.get().preferences.confirmTowerSales) this.showSellConfirmation(tower, economy);
      else this.sellTower(tower);
    });
    c.add([refundText, sellPanel, sellText]);

    // Slide-in tween (from right) - skip when this is just a refresh of the
    // same tower's panel so the UI doesn't flicker.
    if (!wasSameTower) {
      c.setX(40);
      c.setAlpha(0);
      this.tweens.add({ targets: c, x: 0, alpha: 1, duration: 200, ease: "Cubic.easeOut" });
    }

    this.upgradePanel = c;
    this.upgradeAffordable = upCost == null ? null : economy.canAfford(upCost);
  }

  private closeUpgradePanel(): void {
    this.upgradePanel?.destroy();
    this.upgradePanel = undefined;
    this.selectedTower = null;
    this.upgradeAffordable = null;
  }

  private sellTower(tower: Tower): void {
    const soldFor = this.placement.sell(tower);
    if (soldFor == null) return;
    this.closeUpgradePanel();
    sound.playUi("buy");
    this.flashMessage(`${tower.stats.name} sold  +${soldFor}g`, THEME.colors.gold);
  }

  private showSellConfirmation(tower: Tower, economy: Economy): void {
    this.overlayGroup?.destroy();
    this.overlayDismiss = undefined;

    const gameWasPaused = this.scene.isPaused("GameScene");
    if (!gameWasPaused) this.scene.pause("GameScene");

    const c = this.add.container(0, 0).setDepth(THEME.depths.overlay);
    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.68)
      .setOrigin(0).setInteractive();

    const dialogW = 480, dialogH = 280;
    const dialogX = (GAME_WIDTH - dialogW) / 2;
    const dialogY = (GAME_HEIGHT - dialogH) / 2;
    const dialog = panel(this, dialogX, dialogY, dialogW, dialogH, "parchment");
    dialog.setInteractive();

    const refund = economy.quoteSellRefund(tower.totalInvested);
    const refundPct = Math.round(economy.sellRefundPct * 100);
    const title = this.add.text(GAME_WIDTH / 2, dialogY + 38, `Sell ${tower.stats.name}?`, {
      fontFamily: THEME.fonts.display, fontSize: "28px", color: THEME.colors.iron
    }).setOrigin(0.5);
    const message = this.add.text(GAME_WIDTH / 2, dialogY + 88,
      `You will receive ${refund} gold (${refundPct}% of ${tower.totalInvested}g invested).\nThis cannot be undone.`, {
        fontFamily: THEME.fonts.body, fontSize: "17px", color: THEME.colors.iron,
        align: "center", lineSpacing: 6
      }).setOrigin(0.5);

    let dontAskAgain = false;
    const checkX = dialogX + 48;
    const checkY = dialogY + 156;
    const checkHit = this.add.rectangle(checkX, checkY, dialogW - 96, 34, 0x000000, 0)
      .setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
    const checkBox = this.add.rectangle(checkX, checkY, 22, 22, 0xf2e2b8, 1)
      .setOrigin(0, 0.5).setStrokeStyle(2, THEME.hex.ironEdge);
    const checkMark = this.add.text(checkX + 11, checkY, "✓", {
      fontFamily: THEME.fonts.display, fontSize: "18px", color: THEME.colors.blood
    }).setOrigin(0.5).setVisible(false);
    const checkLabel = this.add.text(checkX + 34, checkY, "Don't ask again for tower sales", {
      fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.iron
    }).setOrigin(0, 0.5);
    checkHit.on("pointerdown", () => {
      dontAskAgain = !dontAskAgain;
      checkMark.setVisible(dontAskAgain);
      sound.playUi("click");
    });

    const btnW = 160, btnH = 40, gap = 20;
    const btnY = dialogY + dialogH - btnH - 20;
    const cancelX = GAME_WIDTH / 2 - btnW - gap / 2;
    const sellX = GAME_WIDTH / 2 + gap / 2;
    const cancelPanel = panel(this, cancelX, btnY, btnW, btnH, "iron")
      .setInteractive({ useHandCursor: true });
    const cancelLabel = this.add.text(cancelX + btnW / 2, btnY + btnH / 2, "KEEP TOWER", {
      fontFamily: THEME.fonts.display, fontSize: "15px", color: THEME.colors.gold
    }).setOrigin(0.5);
    const confirmPanel = panel(this, sellX, btnY, btnW, btnH, "iron")
      .setTint(0x87332d).setInteractive({ useHandCursor: true });
    const confirmLabel = this.add.text(sellX + btnW / 2, btnY + btnH / 2, `SELL +${refund}g`, {
      fontFamily: THEME.fonts.display, fontSize: "15px", color: THEME.colors.parchment
    }).setOrigin(0.5);

    let closed = false;
    const cleanup = () => {
      if (closed) return;
      closed = true;
      this.overlayGroup?.destroy();
      this.overlayGroup = undefined;
      if (!gameWasPaused) this.scene.resume("GameScene");
    };

    cancelPanel.on("pointerover", () => cancelPanel.setTint(0xffeec0));
    cancelPanel.on("pointerout",  () => cancelPanel.clearTint());
    cancelPanel.on("pointerdown", () => { sound.playUi("click"); cleanup(); });
    confirmPanel.on("pointerover", () => confirmPanel.setTint(0xa9443b));
    confirmPanel.on("pointerout",  () => confirmPanel.setTint(0x87332d));
    confirmPanel.on("pointerdown", () => {
      if (dontAskAgain) {
        save.get().preferences.confirmTowerSales = false;
        save.markDirty();
      }
      cleanup();
      this.sellTower(tower);
    });

    c.add([
      dim, dialog, title, message, checkHit, checkBox, checkMark, checkLabel,
      cancelPanel, cancelLabel, confirmPanel, confirmLabel
    ]);
    this.overlayGroup = c;
  }

  /**
   * The map's own boss wave just fell. The run is NOT over — Endless continues
   * from here — so this reads as a campaign milestone with a choice, not as a
   * run-end screen.
   */
  private showCampaignSecured(
    p: { wave: number; mapName: string; nextMapName: string | null },
    game: GameScene
  ): void {
    if (this.overlayGroup) this.overlayGroup.destroy();
    const c = this.add.container(0, 0).setDepth(THEME.depths.overlay);
    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7).setOrigin(0).setInteractive();

    const scrollW = 620, scrollH = 340;
    const scrollX = (GAME_WIDTH - scrollW) / 2;
    const scrollY = (GAME_HEIGHT - scrollH) / 2;
    const scroll = panel(this, scrollX, scrollY, scrollW, scrollH, "parchment");

    const title = this.add.text(GAME_WIDTH / 2, scrollY + 78, "REALM SECURED", {
      fontFamily: THEME.fonts.display, fontSize: "52px", color: THEME.colors.gold,
      stroke: THEME.colors.ironDeep, strokeThickness: 4
    }).setOrigin(0.5);
    const sub = this.add.text(GAME_WIDTH / 2, scrollY + 142,
      `${p.mapName} held for ${p.wave} waves.`,
      { fontFamily: THEME.fonts.body, fontSize: "22px", color: THEME.colors.iron }
    ).setOrigin(0.5);
    const unlock = this.add.text(GAME_WIDTH / 2, scrollY + 180,
      p.nextMapName ? `${p.nextMapName} is now open.` : "Every battlefield is yours.",
      { fontFamily: THEME.fonts.body, fontSize: "18px", color: THEME.colors.iron }
    ).setOrigin(0.5);

    const btnW = 240, btnH = 48, gap = 20;
    const btnY = scrollY + scrollH - btnH - 34;
    const endlessX = GAME_WIDTH / 2 - btnW - gap / 2;
    const atlasX = GAME_WIDTH / 2 + gap / 2;
    let clicked = false;
    // Dismiss only: the run stays live and START WAVE keeps working. Shared by
    // the ENTER ENDLESS button and by ESC.
    const dismiss = () => {
      if (clicked) return;
      clicked = true;
      this.overlayDismiss = undefined;
      this.time.delayedCall(0, () => {
        this.overlayGroup?.destroy();
        this.overlayGroup = undefined;
      });
    };

    const endlessPanel = panel(this, endlessX, btnY, btnW, btnH, "iron");
    endlessPanel.setInteractive({ useHandCursor: true });
    const endlessLabel = this.add.text(endlessX + btnW / 2, btnY + btnH / 2, "ENTER ENDLESS", {
      fontFamily: THEME.fonts.display, fontSize: "20px", color: THEME.colors.gold
    }).setOrigin(0.5);
    endlessPanel.on("pointerover", () => { endlessPanel.setTint(0xffeec0); endlessLabel.setColor(THEME.colors.parchment); });
    endlessPanel.on("pointerout",  () => { endlessPanel.clearTint(); endlessLabel.setColor(THEME.colors.gold); });
    endlessPanel.on("pointerdown", () => dismiss());

    const atlasPanel = panel(this, atlasX, btnY, btnW, btnH, "iron");
    atlasPanel.setInteractive({ useHandCursor: true });
    const atlasLabel = this.add.text(atlasX + btnW / 2, btnY + btnH / 2, "MAP SELECT", {
      fontFamily: THEME.fonts.display, fontSize: "20px", color: THEME.colors.gold
    }).setOrigin(0.5);
    atlasPanel.on("pointerover", () => { atlasPanel.setTint(0xffeec0); atlasLabel.setColor(THEME.colors.parchment); });
    atlasPanel.on("pointerout",  () => { atlasPanel.clearTint(); atlasLabel.setColor(THEME.colors.gold); });
    atlasPanel.on("pointerdown", () => {
      if (clicked) return;
      clicked = true;
      this.overlayDismiss = undefined;
      atlasPanel.disableInteractive();
      endlessPanel.disableInteractive();
      this.time.delayedCall(0, () => {
        this.overlayGroup?.destroy();
        this.overlayGroup = undefined;
        game.quitToMapSelect();
      });
    });

    c.add([dim, scroll, title, sub, unlock, endlessPanel, endlessLabel, atlasPanel, atlasLabel]);
    // Same scroll-unroll treatment as the run-end overlay.
    scroll.setScale(1, 0.05);
    const fadeIn = [title, sub, unlock, endlessPanel, endlessLabel, atlasPanel, atlasLabel];
    fadeIn.forEach(o => o.setAlpha(0));
    this.tweens.add({ targets: scroll, scaleY: 1, duration: 350, ease: "Cubic.easeOut" });
    this.tweens.add({ targets: fadeIn, alpha: 1, duration: 250, delay: 320 });

    this.overlayGroup = c;
    this.overlayDismiss = dismiss;
  }

  private showGameOver(victory: boolean, game: GameScene): void {
    if (this.overlayGroup) this.overlayGroup.destroy();
    this.overlayDismiss = undefined;
    const c = this.add.container(0, 0).setDepth(THEME.depths.overlay);
    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.7).setOrigin(0).setInteractive();

    const scrollW = 600, scrollH = 320;
    const scrollX = (GAME_WIDTH - scrollW) / 2;
    const scrollY = (GAME_HEIGHT - scrollH) / 2;
    const scroll = panel(this, scrollX, scrollY, scrollW, scrollH, "parchment");

    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 80,
      victory ? "VICTORY" : "DEFEAT",
      { fontFamily: THEME.fonts.display, fontSize: "60px",
        color: victory ? THEME.colors.gold : THEME.colors.blood,
        stroke: THEME.colors.ironDeep, strokeThickness: 4 }
    ).setOrigin(0.5);
    const sub = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 10,
      victory ? "The realm is safe." : "The keep has fallen.",
      { fontFamily: THEME.fonts.body, fontSize: "22px", color: THEME.colors.iron }
    ).setOrigin(0.5);

    const btnW = 220, btnH = 48, gap = 20;
    const btnY = GAME_HEIGHT / 2 + 60;
    const playX = GAME_WIDTH / 2 - btnW - gap / 2;
    const menuX = GAME_WIDTH / 2 + gap / 2;

    const btnPanel = panel(this, playX, btnY, btnW, btnH, "iron");
    btnPanel.setInteractive({ useHandCursor: true });
    const btnLabel = this.add.text(playX + btnW / 2, btnY + btnH / 2, "PLAY AGAIN", {
      fontFamily: THEME.fonts.display, fontSize: "22px", color: THEME.colors.gold
    }).setOrigin(0.5);
    let clicked = false;
    btnPanel.on("pointerover", () => { btnPanel.setTint(0xffeec0); btnLabel.setColor(THEME.colors.parchment); });
    btnPanel.on("pointerout",  () => { btnPanel.clearTint(); btnLabel.setColor(THEME.colors.gold); });
    btnPanel.on("pointerdown", () => {
      if (clicked) return;
      clicked = true;
      btnPanel.disableInteractive();
      this.time.delayedCall(0, () => {
        this.overlayGroup?.destroy();
        this.overlayGroup = undefined;
        game.restart();
      });
    });

    const fromEditor = this.registry.get("editorPlayTest") === true;
    const menuPanel2 = panel(this, menuX, btnY, btnW, btnH, "iron");
    menuPanel2.setInteractive({ useHandCursor: true });
    const menuLabel2 = this.add.text(menuX + btnW / 2, btnY + btnH / 2, fromEditor ? "BACK TO EDITOR" : "MAIN MENU", {
      fontFamily: THEME.fonts.display, fontSize: fromEditor ? "18px" : "22px", color: THEME.colors.gold
    }).setOrigin(0.5);
    menuPanel2.on("pointerover", () => { menuPanel2.setTint(0xffeec0); menuLabel2.setColor(THEME.colors.parchment); });
    menuPanel2.on("pointerout",  () => { menuPanel2.clearTint(); menuLabel2.setColor(THEME.colors.gold); });
    menuPanel2.on("pointerdown", () => {
      if (clicked) return;
      clicked = true;
      menuPanel2.disableInteractive();
      btnPanel.disableInteractive();
      this.time.delayedCall(0, () => {
        this.overlayGroup?.destroy();
        this.overlayGroup = undefined;
        if (fromEditor) game.quitToEditor();
        else game.quitToMenu();
      });
    });

    c.add([dim, scroll, title, sub, btnPanel, btnLabel, menuPanel2, menuLabel2]);
    // Scroll-unroll tween.
    scroll.setScale(1, 0.05);
    title.setAlpha(0);
    sub.setAlpha(0);
    btnPanel.setAlpha(0);
    btnLabel.setAlpha(0);
    menuPanel2.setAlpha(0);
    menuLabel2.setAlpha(0);
    this.tweens.add({ targets: scroll, scaleY: 1, duration: 350, ease: "Cubic.easeOut" });
    this.tweens.add({ targets: [title, sub, btnPanel, btnLabel, menuPanel2, menuLabel2], alpha: 1, duration: 250, delay: 320 });

    this.overlayGroup = c;
  }

  private confirmQuitToMenu(game: GameScene): void {
    if (this.overlayGroup) this.overlayGroup.destroy();
    this.overlayDismiss = undefined;
    const fromEditor = this.registry.get("editorPlayTest") === true;
    const c = this.add.container(0, 0).setDepth(THEME.depths.overlay);
    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6).setOrigin(0).setInteractive();

    const scrollW = 400, scrollH = 180;
    const scrollX = (GAME_WIDTH - scrollW) / 2;
    const scrollY = (GAME_HEIGHT - scrollH) / 2;
    const scroll = panel(this, scrollX, scrollY, scrollW, scrollH, "parchment");

    const title = this.add.text(GAME_WIDTH / 2, scrollY + 36, fromEditor ? "Return to editor?" : "Quit to menu?", {
      fontFamily: THEME.fonts.display, fontSize: "28px", color: THEME.colors.iron
    }).setOrigin(0.5);
    const sub = this.add.text(GAME_WIDTH / 2, scrollY + 72, "Current run progress will be lost.", {
      fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.parchmentDim
    }).setOrigin(0.5);

    const btnW = 140, btnH = 36, gap = 16;
    const btnY = scrollY + scrollH - btnH - 14;
    const cancelX = GAME_WIDTH / 2 - btnW - gap / 2;
    const confirmX = GAME_WIDTH / 2 + gap / 2;

    const cancelPanel = panel(this, cancelX, btnY, btnW, btnH, "iron");
    cancelPanel.setInteractive({ useHandCursor: true });
    const cancelLabel = this.add.text(cancelX + btnW / 2, btnY + btnH / 2, "CANCEL", {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.parchment
    }).setOrigin(0.5);

    const confirmPanel = panel(this, confirmX, btnY, btnW, btnH, "iron");
    confirmPanel.setInteractive({ useHandCursor: true });
    const confirmLabel = this.add.text(confirmX + btnW / 2, btnY + btnH / 2, fromEditor ? "TO EDITOR" : "QUIT", {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: fromEditor ? THEME.colors.gold : THEME.colors.blood
    }).setOrigin(0.5);

    const cleanup = () => { this.overlayGroup?.destroy(); this.overlayGroup = undefined; };

    cancelPanel.on("pointerover", () => cancelPanel.setTint(0xffeec0));
    cancelPanel.on("pointerout",  () => cancelPanel.clearTint());
    cancelPanel.on("pointerdown", cleanup);

    confirmPanel.on("pointerover", () => confirmPanel.setTint(0xffeec0));
    confirmPanel.on("pointerout",  () => confirmPanel.clearTint());
    confirmPanel.on("pointerdown", () => { cleanup(); if (fromEditor) game.quitToEditor(); else game.quitToMenu(); });

    c.add([dim, scroll, title, sub, cancelPanel, cancelLabel, confirmPanel, confirmLabel]);
    this.overlayGroup = c;
  }

  private tweenNumber(from: number, to: number, duration: number, onUpdate: (v: number) => void): void {
    if (from === to) { onUpdate(to); return; }
    this.tweens.addCounter({
      from, to, duration, ease: "Sine.easeOut",
      onUpdate: tween => onUpdate(Math.round(Number(tween.getValue() ?? to))),
      onComplete: () => onUpdate(to)
    });
  }

  // ---- Tutorial overlay -------------------------------------------------
  private tutorialContainer?: Phaser.GameObjects.Container;
  private tutorialPortrait?: Phaser.GameObjects.Image;
  private tutorialText?: Phaser.GameObjects.Text;
  private tutorialNextLabel?: Phaser.GameObjects.Text;
  private tutorialStep = 0;
  private readonly tutorialSteps: { portrait: "idle" | "speaking"; text: string }[] = [
    { portrait: "idle", text:
      "Welcome, commander. I am the steward of this castle.\n\nDark tides march from the west — we must hold the pass." },
    { portrait: "speaking", text:
      "Enemies follow the stone path from left to right.\nIf any reach our gates, we lose a life. Run out of lives and the realm falls." },
    { portrait: "speaking", text:
      "Below is your tower hotbar. Each tower has a gold cost and a role:\n" +
      " • Archer — fast, single-target\n" +
      " • Cannon — slow splash damage\n" +
      " • Mage   — pierces armor" },
    { portrait: "speaking", text:
      "Click a tower icon (or press 1–8) then click a grass tile to build it.\nClick an existing tower to upgrade or sell it for a partial refund." },
    { portrait: "speaking", text:
      "Slay foes to earn gold. Clear waves to earn XP and unlock skill points\nfor the SKILL TREE in the main menu." },
    { portrait: "speaking", text:
      "When your defences are ready, press START WAVE in the top right.\nSPACE toggles 1x/2x/4x speed. ESC cancels placement." },
    { portrait: "idle", text:
      "Good luck, commander. The realm is in your hands." }
  ];

  private startTutorial(): void {
    if (this.tutorialContainer) return;
    const w = 840, h = 240;
    const x = (GAME_WIDTH - w) / 2;
    const y = GAME_HEIGHT - h - 100;
    const portraitW = 220;
    const portraitH = 198;
    const portraitX = x + 126;
    const portraitY = y + h / 2 + 6;
    const portraitFrameW = 236;
    const portraitFrameH = 214;
    const textX = x + 270;
    const textW = w - 294;

    const c = this.add.container(0, 0).setDepth(THEME.depths.hud + 50);

    // Dim backdrop (click-absorbing so it also blocks placement).
    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.45).setOrigin(0);
    dim.setInteractive();
    c.add(dim);

    // Dialogue panel.
    const box = panel(this, x, y, w, h, "parchment");
    c.add(box);

    const portraitFrame = panel(
      this,
      portraitX - portraitFrameW / 2,
      portraitY - portraitFrameH / 2,
      portraitFrameW,
      portraitFrameH,
      "iron"
    );
    const portrait = this.add.image(portraitX, portraitY, "portrait-idle")
      .setDisplaySize(portraitW, portraitH);
    c.add([portraitFrame, portrait]);
    this.tutorialPortrait = portrait;

    // Speaker name.
    c.add(this.add.text(textX, y + 16, "Castle Steward", {
      fontFamily: THEME.fonts.display, fontSize: "22px", color: THEME.colors.ironDeep
    }));

    // Body text.
    const body = this.add.text(textX, y + 52, "", {
      fontFamily: THEME.fonts.body, fontSize: "18px",
      color: THEME.colors.ironDeep, wordWrap: { width: textW }, lineSpacing: 2
    });
    c.add(body);
    this.tutorialText = body;

    // Next / Skip buttons.
    const btnW = 120, btnH = 34;
    const nextX = x + w - btnW - 16;
    const nextY = y + h - btnH - 14;
    const nextBg = panel(this, nextX, nextY, btnW, btnH, "iron");
    nextBg.setInteractive({ useHandCursor: true });
    const nextLabel = this.add.text(nextX + btnW / 2, nextY + btnH / 2, "NEXT", {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.gold
    }).setOrigin(0.5);
    nextBg.on("pointerover", () => { nextBg.setTint(0xffeec0); nextLabel.setColor(THEME.colors.parchment); });
    nextBg.on("pointerout",  () => { nextBg.clearTint(); nextLabel.setColor(THEME.colors.gold); });
    nextBg.on("pointerdown", () => this.advanceTutorial());
    c.add(nextBg); c.add(nextLabel);
    this.tutorialNextLabel = nextLabel;

    const skipX = x + 16;
    const skipY = nextY;
    const skipBg = panel(this, skipX, skipY, btnW, btnH, "iron");
    skipBg.setInteractive({ useHandCursor: true });
    const skipLabel = this.add.text(skipX + btnW / 2, skipY + btnH / 2, "SKIP", {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.parchmentDim
    }).setOrigin(0.5);
    skipBg.on("pointerover", () => { skipBg.setTint(0xffeec0); skipLabel.setColor(THEME.colors.parchment); });
    skipBg.on("pointerout",  () => { skipBg.clearTint(); skipLabel.setColor(THEME.colors.parchmentDim); });
    skipBg.on("pointerdown", () => this.closeTutorial());
    c.add(skipBg); c.add(skipLabel);

    this.tutorialContainer = c;
    this.tutorialStep = 0;
    this.renderTutorialStep();
  }

  private advanceTutorial(): void {
    this.tutorialStep++;
    if (this.tutorialStep >= this.tutorialSteps.length) {
      this.closeTutorial();
      return;
    }
    this.renderTutorialStep();
  }

  private renderTutorialStep(): void {
    const step = this.tutorialSteps[this.tutorialStep];
    if (!step || !this.tutorialPortrait || !this.tutorialText || !this.tutorialNextLabel) return;
    const key = step.portrait === "idle" ? "portrait-idle" : "portrait-speaking";
    this.tutorialPortrait.setTexture(key);
    this.tutorialPortrait.setDisplaySize(220, 198);
    this.tutorialText.setText(step.text);
    const last = this.tutorialStep === this.tutorialSteps.length - 1;
    this.tutorialNextLabel.setText(last ? "BEGIN" : "NEXT");
  }

  private closeTutorial(): void {
    this.tutorialContainer?.destroy();
    this.tutorialContainer = undefined;
    this.tutorialPortrait = undefined;
    this.tutorialText = undefined;
    this.tutorialNextLabel = undefined;
    this.registry.set("runTutorial", false);
    const data = save.get();
    if (!data.tutorialCompleted) {
      data.tutorialCompleted = true;
      save.markDirty();
    }
  }
}
